import React from "react";
import { Box, Paper, Typography, useMediaQuery } from "@mui/material";
import { useFormikContext, FieldArray } from "formik";
import SectionHeading from "../common/SectionHeading";

const ALL_OPTIONS = [
  "Intraday Trading",
  "Swing Trading",
  "Positional Trading",
  "Commodity Trading",
  "Currency Trading",
  "Only Long Term Investment",
];

const ALL_WITH_MASTER = [...ALL_OPTIONS, "All of the Above"];

const TradingInvestment = ({ formField }) => {
  const { trading, investment, tradingPreferences } = formField;
  const { values, setFieldValue, errors, touched } = useFormikContext();
  //  console.log("form field values trading inv", values);
  const tradingOptions = ["Equity", "Future Option", "Both", "None"];
  const investmentOptions = ["Direct Equity", "Mutual Fund", "Both", "None"];

  const preferencesList = values.tradingAndInvestment?.tradingPreferences || [];
  const isAllSelected = preferencesList.length === ALL_OPTIONS.length;

  const isSmallDevice = useMediaQuery("(max-width:600px)");

  const handleCheckboxChange = (fieldName, optionList, value) => {
    let selected = values[fieldName] || [];
    let individualOptions = optionList.filter((v) => v !== "Both");

    if (value === "Both") {
      // we need to remove none if both is selected and add all individual options
      individualOptions = optionList.filter(
        (v) => v !== "Both" && v !== "None",
      );
      const isAllSelected = individualOptions.every((v) =>
        selected.includes(v),
      );
      selected = isAllSelected ? [] : individualOptions;
    } else {
      if (selected.includes(value)) {
        selected = selected.filter((v) => v !== value);
      } else {
        selected = [...selected, value];
      }
    }

    setFieldValue(fieldName, selected);
  };

  const isBothChecked = (fieldName, optionList) => {
    const individualOptions = optionList.filter((v) => v !== "Both");
    return individualOptions.every((v) => values[fieldName]?.includes(v));
  };

  const handleTradingPreferencesCheckboxChange = (type) => {
    let updatedList = [...preferencesList];

    if (type === "All of the Above") {
      if (isAllSelected) {
        setFieldValue(tradingPreferences.name, []);
      } else {
        setFieldValue(tradingPreferences.name, ALL_OPTIONS);
      }
      return;
    }

    const exists = preferencesList.includes(type);

    if (exists) {
      updatedList = updatedList.filter((item) => item !== type);
    } else {
      updatedList.push(type);
    }

    setFieldValue(tradingPreferences.name, updatedList);
  };

  const renderCheckboxes = (fieldName, options) => {
    const mainField = fieldName.split(".")[0];
    const subField = fieldName.split(".")[1];
    return options.map((option) => {
      const checked =
        option === "Both"
          ? isBothChecked(fieldName, options)
          : values[mainField]?.[subField].includes(option);

      const sanitizedOption = option.replace(/\s+/g, "-");

      return (
        <div className="col-md-12 mb-3 " key={option}>
          <div className="form-check mb-0">
            <input
              type="checkbox"
              className="form-check-input"
              id={`${fieldName}-${sanitizedOption}`}
              checked={checked}
              onChange={() => handleCheckboxChange(fieldName, options, option)}
            />
            <label
              className="form-check-label d-block "
              htmlFor={`${fieldName}-${sanitizedOption}`}
              style={{ fontSize: "inherit" }}
            >
              {option}
            </label>
          </div>
        </div>
      );
    });
  };

  return (
    <Box sx={{ p: 1, maxWidth: 1000, mx: "auto" }}>
      <Paper
        elevation={3}
        sx={{
          p: isSmallDevice ? 2 : 3,
          position: "relative",
          border: "1px solid #e0e0e0",
          borderRadius: 3,
        }}
      >
        {/* <SectionHeading title="Interested In Trading & Investment" number={6} /> */}

        <div>
          {/* Trading */}
          <div className="row mb-3">
            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                mb: 2,
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              1. Interested in Trading: <span className="text-danger">*</span>
            </Typography>
            {renderCheckboxes(trading.name, tradingOptions)}
            {touched[trading.name] && errors[trading.name] && (
              <div className="text-danger">{errors[trading.name]}</div>
            )}
          </div>

          {/* Investment */}
          <div className="row">
            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                mb: 2,
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              2. Interested In Investments:{" "}
              <span className="text-danger">*</span>
            </Typography>
            {renderCheckboxes(investment.name, investmentOptions)}
            {touched[investment.name] && errors[investment.name] && (
              <div className="text-danger">{errors[investment.name]}</div>
            )}
          </div>

          <div className="row">
            <FieldArray name={tradingPreferences.name}>
              {() => (
                <Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      mb: 2,
                      display: "block",
                      textTransform: "capitalize",
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                  >
                    3. Select your trading type(s):
                  </Typography>

                  {ALL_WITH_MASTER.map((type) => {
                    const exists = preferencesList.includes(type);
                    const sanitizedType = type.replace(/\s+/g, "-");

                    return (
                      <div key={type} className="col-md-12 mb-3 ">
                        <div className="form-check mb-0">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`trading-${sanitizedType}`}
                            checked={
                              type === "All of the Above"
                                ? isAllSelected
                                : exists
                            }
                            onChange={() =>
                              handleTradingPreferencesCheckboxChange(type)
                            }
                          />
                          <Typography
                            component="label"
                            htmlFor={`trading-${sanitizedType}`}
                            sx={{
                              display: "block",
                              fontSize: { xs: "0.875rem", sm: "1rem" },
                              cursor: "pointer",
                            }}
                          >
                            {type}
                          </Typography>
                        </div>
                      </div>
                    );
                  })}
                </Box>
              )}
            </FieldArray>
          </div>
        </div>
      </Paper>
    </Box>
  );
};

export default TradingInvestment;
